import btoa from "btoa";
import { isBrowser } from "../browser-utils";

const getAuthHeader = (): string => {
	const clientId = process.env.GATSBY_COMMERCETOOLS_FRONTEND_CLIENT_ID;
	const clientSecret = process.env.GATSBY_COMMERCETOOLS_FRONTEND_CLIENT_SECRET;

	return `Basic ${(isBrowser() ? window.btoa : btoa)(
		`${clientId}:${clientSecret}`,
	)}`;
};

export type CommercetoolsSession = {
	accessToken: string;
	refreshToken: string;
	/**
	 * Timestamp in seconds when the access token expires.
	 */
	expires: number;
};

export const isCommercetoolsSession = (
	session: unknown,
): session is CommercetoolsSession => {
	if (typeof session !== "object" || session === null) return false;

	const { accessToken, refreshToken, expires } =
		session as CommercetoolsSession;

	return (
		typeof accessToken === "string" &&
		typeof refreshToken === "string" &&
		typeof expires === "number"
	);
};

let authPromise: Promise<CommercetoolsSession> | undefined;

export const authenticate = async (
	session?: CommercetoolsSession,
): Promise<CommercetoolsSession> => {
	if (authPromise) return authPromise;

	authPromise = session
		? refreshAccessToken(session.refreshToken).finally(() => {
				authPromise = undefined;
			})
		: performAuthentication().finally(() => {
				authPromise = undefined;
			});

	return authPromise;
};

/**
 * Get a new access token.
 */
const performAuthentication = async (): Promise<CommercetoolsSession> => {
	const host = process.env.GATSBY_COMMERCETOOLS_AUTH_URL;
	const projectKey = process.env.GATSBY_COMMERCETOOLS_PROJECT_KEY;
	const scope = process.env.GATSBY_COMMERCETOOLS_FRONTEND_SCOPES;

	const res = await fetch(
		new URL(`/oauth/${projectKey}/anonymous/token`, host).href,
		{
			body: `grant_type=client_credentials&scope=${scope}`,
			headers: {
				Authorization: getAuthHeader(),
				"Content-Type": "application/x-www-form-urlencoded",
			},
			method: "POST",
		},
	);

	const json = (await res.json()) as {
		access_token: string;
		refresh_token: string;
		expires_in: number;
	};

	const accessToken: string = json.access_token;
	const refreshToken: string = json.refresh_token;
	const expiresIn: number = json.expires_in;

	const expires = Math.floor(Date.now() / 1000) + expiresIn;

	return { accessToken, refreshToken, expires };
};

/**
 * Refresh the access token.
 */
const refreshAccessToken = async (
	refreshToken: string,
): Promise<CommercetoolsSession> => {
	const host = process.env.GATSBY_COMMERCETOOLS_AUTH_URL;

	const response = await fetch(new URL("/oauth/token", host).href, {
		body: `grant_type=refresh_token&refresh_token=${refreshToken}`,
		headers: {
			Authorization: getAuthHeader(),
			"Content-Type": "application/x-www-form-urlencoded",
		},
		method: "POST",
	});

	if (!response.ok) {
		return await performAuthentication();
	}

	const json = (await response.json()) as {
		access_token: string;
		expires_in: number;
	};

	const accessToken = json.access_token;
	const expiresIn = json.expires_in;

	const expires = Math.floor(Date.now() / 1000) + expiresIn;

	return { accessToken, refreshToken, expires };
};
